import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, scrollToHash } from "@/components/Reveal";

export const HERO_IMG =
  "https://images.unsplash.com/photo-1568347877321-f8935c7dc5a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGFlcmlhbHxlbnwwfHx8fDE3ODY1NTg2MTF8MA&ixlib=rb-4.1.0&q=85&w=2400";

const LINES = ["WE MOVE", "THE WORLD", "FORWARD"];

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex h-[100svh] min-h-[680px] flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Container ship crossing open ocean at night"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
          className="h-[125%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/70" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 px-6 pb-10 lg:px-12 lg:pb-14"
      >
        <div className="mb-6 overflow-hidden">
          <motion.p
            initial={{ y: "120%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
            data-testid="hero-overline"
            className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-white/60"
          >
            <span className="h-px w-10 bg-[#FF3B30]" />
            International Logistics - Est. 2024
          </motion.p>
        </div>

        <h1 className="font-display font-semibold uppercase leading-[0.85] tracking-tighter">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.2, delay: 0.35 + i * 0.13, ease: EASE }}
                className="block text-[14vw] sm:text-[12vw] lg:text-[8.8vm]"
                data-testid={`hero-line-${i + 1}`}
              >
                {i === 2 ? (
                  <>
                    Forward<span className="text-[#FF3B30]">.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            data-testid="hero-subtitle"
            className="max-w-md text-base leading-relaxed text-white/70 md:text-lg"
          >
            Air, ocean and land freight across 70+ countries. One partner,
            total visibility, zero excuses.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-quote"
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("#contact");
              }}
              className="group relative overflow-hidden bg-[#FF3B30] px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white"
            >
              <span className="absolute inset-0 translate-y-full bg-white transition-transform duration-300 ease-out group-hover:translate-y-0" />
              <span className="relative z-10 flex items-center gap-2 transition-colors duration-300 group-hover:text-black">
                Start a shipment
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </span>
            </a>
            <a
              href="#services"
              data-testid="hero-cta-services"
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("#services");
              }}
              className="group relative overflow-hidden border border-white/25 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white"
            >
              <span className="absolute inset-0 translate-y-full bg-white/10 transition-transform duration-300 ease-out group-hover:translate-y-0" />
              <span className="relative z-10">Explore services</span>
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-10 grid grid-cols-3 border-t border-white/15 pt-5"
          data-testid="hero-strip"
        >
          {[
            ["EST.", "2024 - REWA"],
            ["LANES", "70+ countries"],
            ["OPS", "24/7 command"],
          ].map(([k, v]) => (
            <div key={k} className="text-xs uppercase tracking-[0.2em]">
              <span className="text-[#FF3B30]">{k}</span>
              <span className="ml-2 text-white/50">{v}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
